import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import StatusBadge from "@/components/common/StatusBadge";
import { ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  LEGAL_FORM_LABELS, STATUS_LABELS, MANAGEMENT_MODE_LABELS, getLegalFormTone,
} from "@/constants/group";
import CompanyIdentityTab from "./tabs/CompanyIdentityTab";
import CompanyShareholdersTab from "./tabs/CompanyShareholdersTab";
import CompanyFlowsTab from "./tabs/CompanyFlowsTab";
import CompanyDocumentsTab from "./tabs/CompanyDocumentsTab";
import CompanyCompletenessTab from "./tabs/CompanyCompletenessTab";
import { useAuth } from "@/lib/auth";

export default function CompanyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activeRole } = useAuth();
  const canDelete = ["owner", "admin"].includes(activeRole);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/companies/${id}`); setCompany(data); }
    catch { toast.error("Société introuvable"); navigate("/app/companies/entities"); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const remove = async () => {
    if (!window.confirm("Supprimer définitivement cette entité, ses participations et ses flux ?")) return;
    try {
      await api.delete(`/companies/${id}`);
      toast.success("Entité supprimée");
      navigate("/app/companies/entities");
    } catch { toast.error("Suppression impossible"); }
  };

  if (loading || !company) return <div className="text-muted-foreground text-sm">Chargement…</div>;

  const tone = getLegalFormTone(company.legal_form);

  return (
    <div className="animate-fade-up">
      <div className="mb-6">
        <Button asChild variant="ghost" size="sm">
          <Link to="/app/companies/entities" data-testid="entity-back-btn"><ArrowLeft className="mr-2 h-4 w-4" /> Retour aux entités</Link>
        </Button>
      </div>

      <div className="card-elevated p-6 lg:p-8 mb-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <div className={`inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-semibold ${tone.bg} ${tone.text} ${tone.border}`}>
              {LEGAL_FORM_LABELS[company.legal_form] || company.legal_form}
            </div>
            <h1 className="mt-3 font-serif text-3xl sm:text-4xl">{company.denomination}</h1>
            {company.trade_name && <div className="text-muted-foreground text-sm mt-1">Nom commercial : {company.trade_name}</div>}
            {company.activity && <div className="text-muted-foreground text-sm mt-1 italic">{company.activity}</div>}
            <div className="mt-4 flex flex-wrap gap-2">
              <StatusBadge tone={company.status === "active" ? "success" : company.status === "archived" ? "neutral" : "warning"}>
                {STATUS_LABELS[company.status || "active"]}
              </StatusBadge>
              <StatusBadge tone={company.management_mode === "reference" ? "warning" : "info"}>
                {MANAGEMENT_MODE_LABELS[company.management_mode || "full"]}
              </StatusBadge>
              {company.tax_regime && <StatusBadge tone="neutral">{company.tax_regime}</StatusBadge>}
            </div>
          </div>
          <div className="flex gap-2">
            {canDelete && (
              <Button variant="outline" onClick={remove} data-testid="entity-delete-btn">
                <Trash2 className="mr-2 h-4 w-4 text-destructive" /> Supprimer
              </Button>
            )}
          </div>
        </div>
      </div>

      <Tabs defaultValue="identity">
        <TabsList className="mb-6 flex-wrap">
          <TabsTrigger value="identity" data-testid="detail-tab-identity">Fiche d'identité</TabsTrigger>
          <TabsTrigger value="shareholders" data-testid="detail-tab-shareholders">Participations</TabsTrigger>
          <TabsTrigger value="flows" data-testid="detail-tab-flows">Flux financiers</TabsTrigger>
          <TabsTrigger value="documents" data-testid="detail-tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="completeness" data-testid="detail-tab-completeness">Complétude</TabsTrigger>
        </TabsList>
        <TabsContent value="identity"><CompanyIdentityTab company={company} onSaved={load} /></TabsContent>
        <TabsContent value="shareholders"><CompanyShareholdersTab company={company} /></TabsContent>
        <TabsContent value="flows"><CompanyFlowsTab company={company} /></TabsContent>
        <TabsContent value="documents"><CompanyDocumentsTab company={company} /></TabsContent>
        <TabsContent value="completeness"><CompanyCompletenessTab company={company} /></TabsContent>
      </Tabs>
    </div>
  );
}
